/** Tiny hash router: "#/frame/3?x=1" -> { name, params, query }. */

export interface Route {
  name: "start" | "join" | "group" | "frame" | "film" | "final";
  position: number;
  query: URLSearchParams;
}

type Listener = (route: Route) => void;

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "");
  const [pathPart, queryPart = ""] = raw.split("?");
  const query = new URLSearchParams(queryPart);
  const segments = pathPart.split("/").filter(Boolean);
  const head = segments[0] ?? "start";
  if (head === "join") return { name: "join", position: 1, query };
  if (head === "group") return { name: "group", position: 1, query };
  if (head === "film") return { name: "film", position: 1, query };
  if (head === "final") return { name: "final", position: 1, query };
  if (head === "frame") {
    // Only garbage-input sanitizing here: the real upper bound is the
    // frame count from the definitions, which are not loaded yet when
    // the first route is parsed. The screens clamp to it themselves.
    const position = Math.min(
      999,
      Math.max(1, parseInt(segments[1] ?? "1", 10) || 1),
    );
    return { name: "frame", position, query };
  }
  return { name: "start", position: 1, query };
}

export class Router {
  #listeners: Listener[] = [];
  #onHashChange = () => this.#emit();

  start(): void {
    window.addEventListener("hashchange", this.#onHashChange);
    this.#emit();
  }

  stop(): void {
    window.removeEventListener("hashchange", this.#onHashChange);
  }

  get route(): Route {
    return parseHash(window.location.hash);
  }

  navigate(to: string): void {
    const target = to.startsWith("#") ? to : `#${to}`;
    if (window.location.hash === target) {
      this.#emit();
    } else {
      window.location.hash = target;
    }
  }

  onChange(listener: Listener): () => void {
    this.#listeners.push(listener);
    return () => {
      this.#listeners = this.#listeners.filter((l) => l !== listener);
    };
  }

  #emit(): void {
    const route = this.route;
    for (const listener of [...this.#listeners]) listener(route);
  }
}

export const router = new Router();
